import React, { useCallback, useState } from "react";
import { Button, ErrorMessage, SelectableCards, Text } from "@jsq/ds/core";
import { PaymentMethod } from "../../../../common/enums";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { type ChildComponentProps } from "../types";
import * as styles from "./styles_vanilla.css";
import { type PaymentOption } from "./types";

export const SelectPaymentMethod: React.FC<ChildComponentProps> = ({
  onBackButtonName = "Back",
  onContinueClick,
  onBackClick,
}) => {
  const { paymentMethods, selectedPaymentMethod, setSelectedPaymentMethod } =
    useConsolidatedPaymentPrefContext();

  const [selected, setSelected] = useState<string | number | undefined>(selectedPaymentMethod);
  const [showError, setShowError] = useState(false);

  const handleFormSubmission = useCallback(() => {
    if (!selected) {
      setShowError(true);
      return;
    }

    setSelectedPaymentMethod(selected as PaymentMethod);
    onContinueClick();
  }, [onContinueClick, selected, setSelectedPaymentMethod]);

  const paymentOptions: PaymentOption[] = [
    {
      title: "ACH direct deposit",
      details: "Requires a US bank account",
      icon: "university",
      value: PaymentMethod.ACH,
    },
    {
      title: "Wire transfer",
      details: "Supports SWIFT wires",
      icon: "money-bill-transfer",
      value: PaymentMethod.Wire,
    },
    {
      title: "Check",
      details: "Requires an address",
      icon: "money-check",
      value: PaymentMethod.Check,
    },
  ];

  // Adjust options based on availablePaymentMethods
  const adjustedOptions = paymentOptions.map((option) => {
    const paymentMethodValue = paymentMethods?.[option.value];
    return {
      ...option,
      disabled: !paymentMethodValue?.isAvailable,
    };
  });

  const hasAvailablePaymentMethods = adjustedOptions.find((option) => !option.disabled);

  return (
    <>
      <Text className={styles.title} variant="h3">
        Create new payment instructions
      </Text>
      <Text variant="h4">Select payment method</Text>
      <SelectableCards
        value={selected}
        onSelectOption={setSelected}
        className={styles.options}
        id="paymentPrefSelectPaymentMethod"
        options={adjustedOptions}
      />
      {!hasAvailablePaymentMethods && (
        <ErrorMessage>No supported payment methods available.</ErrorMessage>
      )}
      {showError && !selected && (
        <ErrorMessage>You must select a payment method to continue</ErrorMessage>
      )}
      <div className={styles.footer}>
        <Button
          className={styles.backButton}
          variant="neutral"
          id="paymentPrefSelectPaymentsBackButton"
          onClick={onBackClick}
        >
          {onBackButtonName}
        </Button>
        <Button
          variant="primary"
          id="paymentPrefSelectPaymentsContinueButton"
          onClick={handleFormSubmission}
          disabled={!hasAvailablePaymentMethods}
        >
          Continue
        </Button>
      </div>
    </>
  );
};
